import type { CSSProperties, ReactElement } from 'react';

export type IconName =
  | 'search' | 'plus' | 'chevdown' | 'chevright' | 'chevleft'
  | 'chevron-down' | 'chevron-right' | 'chevron-left'
  | 'arrow' | 'arrowleft' | 'filter' | 'sort' | 'more'
  | 'bell' | 'settings' | 'home' | 'ticket' | 'users' | 'folder'
  | 'invoice' | 'chart' | 'book' | 'clock' | 'check' | 'checkdouble'
  | 'x' | 'close' | 'eye' | 'paperclip' | 'send' | 'download' | 'refresh'
  | 'flag' | 'timer' | 'user' | 'lock' | 'mail' | 'shield' | 'grid'
  | 'building' | 'spark' | 'info' | 'repeat' | 'layers' | 'star' | 'bolt'
  | 'lightbulb' | 'edit' | 'share' | 'trend-up' | 'calendar' | 'phone'
  | 'tag' | 'pin' | 'message' | 'alert' | 'menu';

export interface IconProps {
  name: IconName;
  size?: number;
  stroke?: number;
  style?: CSSProperties;
  className?: string;
}

const PATHS: Record<IconName, ReactElement> = {
  search:    <><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></>,
  plus:      <path d="M12 5v14M5 12h14"/>,
  chevdown:  <path d="m6 9 6 6 6-6"/>,
  chevright: <path d="m9 6 6 6-6 6"/>,
  chevleft:  <path d="m15 6-6 6 6 6"/>,
  'chevron-down':  <path d="m6 9 6 6 6-6"/>,
  'chevron-right': <path d="m9 6 6 6-6 6"/>,
  'chevron-left':  <path d="m15 6-6 6 6 6"/>,
  arrow:     <path d="M5 12h14M13 5l7 7-7 7"/>,
  arrowleft: <path d="M19 12H5M11 5l-7 7 7 7"/>,
  filter:    <path d="M3 6h18M6 12h12M10 18h4"/>,
  sort:      <path d="M3 6h13M3 12h9M3 18h5M17 8l4-4 4 4M21 4v16"/>,
  more:      <><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></>,
  bell:      <><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10 21a2 2 0 0 0 4 0"/></>,
  settings:  <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></>,
  home:      <path d="m3 10 9-7 9 7v10a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2z"/>,
  ticket:    <><path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4z"/><path d="M9 6v12"/></>,
  users:     <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></>,
  folder:    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>,
  invoice:   <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><path d="M14 3v6h6M9 13h6M9 17h4"/></>,
  chart:     <><path d="M3 3v18h18"/><path d="m7 14 4-4 4 4 5-5"/></>,
  book:      <><path d="M2 4a2 2 0 0 1 2-2h4a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 4a2 2 0 0 0-2-2h-4a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></>,
  clock:     <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
  check:     <path d="m5 12 5 5 9-12"/>,
  checkdouble: <path d="m2 12 5 5 9-12M13 17l9-12"/>,
  x:         <path d="M18 6 6 18M6 6l12 12"/>,
  close:     <path d="M18 6 6 18M6 6l12 12"/>,
  eye:       <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></>,
  paperclip: <path d="m21 12-8.5 8.5a5 5 0 0 1-7-7L14 5a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L15 8"/>,
  send:      <><path d="m22 2-7 20-4-9-9-4z"/><path d="M22 2 11 13"/></>,
  download:  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>,
  refresh:   <><path d="M21 12a9 9 0 0 1-15 6.7L3 16M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M21 3v5h-5M3 21v-5h5"/></>,
  flag:      <path d="M4 22V4a1 1 0 0 1 1-1h12l-2 5 2 5H5"/>,
  timer:     <><circle cx="12" cy="13" r="8"/><path d="M12 9v4l2 2M9 2h6"/></>,
  user:      <><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></>,
  lock:      <><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></>,
  mail:      <><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></>,
  shield:    <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z"/>,
  grid:      <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>,
  building:  <><rect x="4" y="3" width="16" height="18" rx="1"/><path d="M9 8h2M13 8h2M9 12h2M13 12h2M9 16h2M13 16h2"/></>,
  spark:     <path d="m3 17 5-7 4 5 4-7 5 6"/>,
  info:      <><circle cx="12" cy="12" r="9"/><path d="M12 8h.01M11 12h1v5h1"/></>,
  repeat:    <><path d="M17 2l4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="M7 22l-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/></>,
  layers:    <><path d="m12 2 9 5-9 5-9-5z"/><path d="m3 12 9 5 9-5M3 17l9 5 9-5"/></>,
  star:      <path d="m12 2 3 7 7.5.6-5.7 5 1.7 7.4L12 18l-6.5 4 1.7-7.4-5.7-5L9 9z"/>,
  bolt:      <path d="M13 2 3 14h8l-1 8 10-12h-8z"/>,
  lightbulb: <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.7.6 1 1.4 1 2.3h6c0-.9.3-1.7 1-2.3A7 7 0 0 0 12 2z"/>,
  edit:      <><path d="M11 4H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h13a2 2 0 0 0 2-2v-6"/><path d="M19 3a2 2 0 0 1 3 3l-9 9-4 1 1-4z"/></>,
  share:     <><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="m9 10 6-3M9 14l6 3"/></>,
  'trend-up': <><path d="m3 17 6-6 4 4 8-8"/><path d="M14 7h7v7"/></>,
  calendar:  <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></>,
  phone:     <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.4-1.4a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z"/>,
  tag:       <><path d="M20.6 13.4 13 21l-9-9V5h7l9 9-2.4 2.4z"/><circle cx="7.5" cy="7.5" r="1"/></>,
  pin:       <path d="M12 2v6M9 8h6l1 4-5 5-5-5z M12 13v9"/>,
  message:   <path d="M21 11.5a8 8 0 0 1-15 4l-3 1 1-3a8 8 0 1 1 17-2z"/>,
  alert:     <><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/></>,
  menu:      <path d="M4 6h16M4 12h16M4 18h16"/>,
};

export function Icon({ name, size = 16, stroke = 1.75, style, className }: IconProps) {
  const baseStyle: CSSProperties = {
    width: size,
    height: size,
    display: 'inline-block',
    flexShrink: 0,
    ...style,
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={baseStyle}
      className={className}
      aria-hidden="true"
    >
      {PATHS[name]}
    </svg>
  );
}
